import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";

/**
 * Read-only renderer for the diag_secu rich-text subset (labels & disclaimers).
 *
 * Content is stored as CommonMark markdown limited, by convention, to: paragraphs / line breaks,
 * bold, italic, and bullet / numbered lists (see docs/plans rich-text plan). We render exactly
 * that subset and nothing else:
 *
 *  - `remark-breaks` turns a single newline into a visible line break, so legacy plain-text
 *    content (disclaimers historically stored with raw "\n") renders identically to before and
 *    the "return carriage" authoring intent shows through without requiring double newlines.
 *  - `allowedElements` whitelists the subset; anything outside it (headings, links, tables, raw
 *    HTML, …) is stripped to its text content rather than rendered, so out-of-subset or
 *    hand-edited content degrades gracefully instead of breaking layout or injecting markup.
 *  - raw HTML is never parsed (react-markdown ignores it by default — no `rehype-raw`), so there
 *    is no XSS surface.
 */
export default function MarkdownView({
  children,
  className,
}: {
  children: string | null | undefined;
  className?: string;
}) {
  const value = children?.trim();
  if (!value) {
    return null;
  }

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkBreaks]}
        allowedElements={["p", "br", "strong", "em", "ul", "ol", "li"]}
        unwrapDisallowed
        components={{
          p: ({ children }) => <p className="whitespace-pre-line">{children}</p>,
          ul: ({ children }) => <ul className="list-disc pl-5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5">{children}</ol>,
        }}
      >
        {value}
      </ReactMarkdown>
    </div>
  );
}
